import React, { useEffect, useRef, useState } from 'react';
import {
  useSchedulerShiftsListQuery,
  SchedulerShiftsListQuery,
} from '../../../__generated__/graphql';
import { BryntumCalendar, BryntumCalendarProps } from '@bryntum/calendar-react';
import {
  Calendar,
  CalendarView,
  EventModel,
  EventModelConfig,
} from '@bryntum/calendar';
import { CalendarViewMode, ShiftBreak } from './types';
import { getDateRangeForMode } from './utils';
import { mapLocaleToBryntumLocale } from './localeUtils';
import { useUserInfo } from '../../../context/UserInfo';
import ServerError from '../../../../../components/ServerError';
import CalendarLoading from './CalendarLoading';
import './styles.css';
import { EventContent } from './EventContent';
import { ShiftSidePanel } from './ShiftSidePanel';
import { ShiftLabel } from './types';
import { useCurrentLocale } from '../../../../../context/I18n';
import ModalSidebar, {
  ModalSidebarContent,
} from '../../../components/ModalSidebar';

type EventModalData = EventModel & {
  targetRecordName: string;
  shiftLabels: ShiftLabel[];
  breakPeriodMinutes?: number;
  breaks?: ShiftBreak[];
  employeeNotes?: string;
  hasPendingChangeRequests?: boolean;
};

type ShiftsCalendarProps = {
  currentDay: Date;
  mode: CalendarViewMode;
  onDateChange: (date: Date) => void;
  onModeChange: (mode: CalendarViewMode) => void;
  data: SchedulerShiftsListQuery;
  loading: boolean;
};

const ShiftsCalendarView: React.FC<ShiftsCalendarProps> = ({
  currentDay,
  mode,
  onDateChange,
  onModeChange,
  data,
  loading,
}) => {
  const locale = useCurrentLocale();
  const calendarRef = useRef<BryntumCalendar>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventModalData | null>(
    null,
  );

  const handleDateChange = (event: {
    source: Calendar;
    oldDate: Date;
    date: Date;
  }) => {
    onDateChange(event.date);
  };

  const handleActiveItemChange = ({
    source,
  }: {
    source: Calendar;
    prevActiveItem: CalendarView;
    activeItem: CalendarView;
    containedFocus: boolean;
  }) => {
    const newMode = source.mode;
    if (newMode && newMode !== mode) {
      onModeChange(newMode);
    }
  };

  useEffect(() => {
    const calendar = calendarRef.current?.instance;
    if (calendar) {
      const bryntumLocaleName = mapLocaleToBryntumLocale(locale);
      calendar.localeManager.locale = bryntumLocaleName;
    }
  }, [locale]);

  useEffect(() => {
    if (!loading && data?.schedulerShifts) {
      const bryntumEvents: EventModelConfig[] = data.schedulerShifts.map(
        (shift) => ({
          id: shift.Id,
          name: shift.flair__Title__c || '',
          startDate: new Date(shift.flair__Start_Datetime__c),
          endDate: new Date(shift.flair__End_Datetime__c),
          resourceId: shift.flair__Linked_Resource_Id__c,
          targetRecordName:
            shift.location?.Name || shift.department?.Name || '',
          breakPeriodMinutes:
            shift.flair__Total_Break_Period_in_Minutes__c || 0,
          breaks:
            shift.breaks?.map((breakItem) => ({
              id: breakItem.Id,
              startDate: new Date(breakItem.flair__Start_Datetime__c),
              endDate: new Date(breakItem.flair__End_Datetime__c),
            })) || [],
          employeeNotes: shift.flair__Employee_Notes__c || '',
          hasPendingChangeRequests:
            shift.flair__Pending_Change_Requests_Count__c &&
            shift.flair__Pending_Change_Requests_Count__c > 0,
          shiftLabels:
            shift.shiftLabels?.map((label) => ({
              id: label.Id,
              name: label.Name,
              tagName: label.flair__Tag_Name__c,
            })) || [],
        }),
      );

      const calendar = calendarRef.current?.instance;
      if (calendar) {
        calendar.eventStore.data = bryntumEvents;
      }
    }
  }, [data, loading]);

  const customEventRenderer = (detail: { eventRecord: EventModel }) => {
    const { eventRecord } = detail;
    const event = eventRecord as EventModalData;
    return (
      <EventContent
        name={event.name || ''}
        targetRecordName={event.targetRecordName || ''}
        shiftLabels={event?.shiftLabels}
        breakPeriodMinutes={event.breakPeriodMinutes}
        employeeNotes={event.employeeNotes}
        hasPendingChangeRequests={event.hasPendingChangeRequests || false}
      />
    );
  };

  const handleEventClick = (event: { eventRecord: EventModel }) => {
    const eventRecord = event.eventRecord as EventModalData;
    setSelectedEvent(eventRecord);
  };

  const handleCloseSidebar = () => {
    setSelectedEvent(null);
  };

  const calendarConfig: BryntumCalendarProps = {
    date: currentDay,
    mode,
    onDateChange: handleDateChange,
    onActiveItemChange: handleActiveItemChange,
    onEventClick: handleEventClick,
    readOnly: true,
    eventEditFeature: false,
    eventTooltipFeature: false,
    modes: {
      day: {
        showAllDayHeader: false,
        eventRenderer: (data) => customEventRenderer(data),
      },
      week: {
        showAllDayHeader: false,
        eventRenderer: (data) => customEventRenderer(data),
      },
    },
  };

  return (
    <>
      <div className="d-flex flex-column calendar-height">
        <BryntumCalendar ref={calendarRef} {...calendarConfig} />
      </div>
      <ModalSidebar
        show={!!selectedEvent}
        onClose={handleCloseSidebar}
        header={selectedEvent?.name || ''}
        content={
          selectedEvent && (
            <ModalSidebarContent
              body={<ShiftSidePanel shiftId={selectedEvent.id.toString()} />}
            />
          )
        }
      />
    </>
  );
};

export const SchedulerShiftsCalendar: React.FC = () => {
  const authUserInfo = useUserInfo();

  const [currentDay, setCurrentDay] = useState(new Date());
  const [mode, setMode] = useState<CalendarViewMode>('week');

  const { startDate, endDate } = getDateRangeForMode(currentDay, mode);

  const { data, loading, error } = useSchedulerShiftsListQuery({
    variables: {
      filter: { startDate, endDate, employeesIds: [authUserInfo.id] },
    },
  });

  if (loading) {
    return <CalendarLoading currentMode={mode} />;
  }

  if (!data || error) {
    return <ServerError />;
  }

  return (
    <ShiftsCalendarView
      currentDay={currentDay}
      mode={mode}
      onDateChange={setCurrentDay}
      onModeChange={setMode}
      data={data}
      loading={loading}
    />
  );
};
